import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import PipelineProfileCloud, { PipelineProfile } from '../components/PipelineProfileCloud';
import FundingMbcbActions from '../components/FundingMbcbActions';
import StageEmailModal from '../components/StageEmailModal';
import {
  getPlanterPipelineOverview,
  PlanterPipelineOverviewData,
  type PipelineEmailBucket,
} from '../api/plantersService';
import { useAuth } from '../contexts/AuthContext';

function EmailGroupButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Email all ${label} planters`}
      className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.6}
        stroke="currentColor"
        className="h-3.5 w-3.5"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
        />
      </svg>
      Email
    </button>
  );
}

function segmentFlex(count: number, total: number): number {
  if (total <= 0) return 1;
  return Math.max(count, 0.05);
}

const formatUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    n || 0,
  );

const formatInt = (n: number) => new Intl.NumberFormat('en-US').format(n || 0);

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 shadow-sm">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-lg sm:text-xl font-semibold text-gray-900 tabular-nums">{value}</div>
      {hint ? <p className="mt-1 text-xs text-gray-500 leading-snug">{hint}</p> : null}
    </div>
  );
}

const PlanterPipelineOverview: React.FC = () => {
  const { user } = useAuth();
  const hasRole = (roleName: string) =>
    !!user?.roles?.some((r: string | { name: string }) =>
      typeof r === 'string' ? r === roleName : r.name === roleName,
    );
  const canView = hasRole('admin') || hasRole('staff') || hasRole('hub_leader');

  const [emailStage, setEmailStage] = useState<{ bucket: PipelineEmailBucket; label: string } | null>(
    null,
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['planterPipelineOverview', { includeProfiles: true }],
    queryFn: () => getPlanterPipelineOverview({ includeProfiles: true }),
    enabled: canView,
  });

  const overview: PlanterPipelineOverviewData | undefined = data?.data;
  const profiles = overview?.profiles;
  const nm = overview?.networkMetrics;

  const errStatus = (error as { response?: { status?: number } })?.response?.status;

  const portalProfilesFor = (key: string): PipelineProfile[] =>
    (profiles?.portal?.[key] as PipelineProfile[]) || [];

  const planterProfiles = profiles?.planters;

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Planter pipeline</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Planters and pathway first, then portal candidates without a church planter profile, then network
            metrics. Profile clouds give a quick people view; colored bars link to the filtered directory or
            onboarding list.
          </p>
        </div>

        {!canView && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            This overview is available to hub staff (admin, staff, or hub leader).
          </div>
        )}

        {canView && isLoading && (
          <div className="text-center py-16 text-gray-500">Loading pipeline…</div>
        )}

        {canView && errStatus === 403 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            You do not have permission to view pipeline statistics.
          </div>
        )}

        {canView && error && errStatus !== 403 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            Could not load pipeline overview. Try again later.
          </div>
        )}

        {canView && overview && (
          <div className="space-y-10">
            <section className="bg-white shadow-sm rounded-xl p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Planters</h2>
                <span className="text-sm text-gray-500">{overview.planters.activeTotal} active</span>
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-200 min-h-[52px] mb-4">
                {(() => {
                  const t = overview.planters.activeTotal || 1;
                  const segments: { label: string; count: number; to: string; color: string }[] = [
                    {
                      label: 'Growth',
                      count: overview.planters.active.growth,
                      to: '/planters?pathKind=growth&journeyEnded=false',
                      color: '#7c3aed',
                    },
                    {
                      label: 'Training',
                      count: overview.planters.active.training,
                      to: '/planters?pathKind=planting&plantingStep=training&journeyEnded=false',
                      color: '#ca8a04',
                    },
                    {
                      label: 'Funding',
                      count: overview.planters.active.funding,
                      to: '/planters?effectivePlantingStep=funding&journeyEnded=false',
                      color: '#ea580c',
                    },
                    {
                      label: 'Post-funding',
                      count: overview.planters.active.postFunding,
                      to: '/planters?effectivePlantingStep=post_funding&journeyEnded=false',
                      color: '#16a34a',
                    },
                  ];
                  return segments.map((s) => (
                    <Link
                      key={s.label}
                      to={s.to}
                      className="flex items-center justify-center px-2 py-3 text-center text-sm font-medium text-white transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                      style={{ flex: segmentFlex(s.count, t), backgroundColor: s.color }}
                      title={`${s.label}: ${s.count}`}
                    >
                      <span className="leading-tight">
                        <span className="block text-lg font-bold">{s.count}</span>
                        <span className="block text-xs font-normal opacity-95">{s.label}</span>
                      </span>
                    </Link>
                  ));
                })()}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium text-gray-600">Growth</div>
                    <EmailGroupButton
                      label="Growth"
                      onClick={() => setEmailStage({ bucket: 'growth', label: 'Growth' })}
                    />
                  </div>
                  <PipelineProfileCloud
                    profiles={planterProfiles?.growth || []}
                    title="Growth path"
                    listHref="/planters?pathKind=growth&journeyEnded=false"
                    accentClassName="border-violet-200 bg-violet-50/60"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium text-gray-600">Training</div>
                    <EmailGroupButton
                      label="Training"
                      onClick={() => setEmailStage({ bucket: 'training', label: 'Training' })}
                    />
                  </div>
                  <PipelineProfileCloud
                    profiles={planterProfiles?.training || []}
                    title="Training"
                    listHref="/planters?pathKind=planting&plantingStep=training&journeyEnded=false"
                    accentClassName="border-amber-200 bg-amber-50/60"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium text-gray-600">Funding</div>
                    <EmailGroupButton
                      label="Funding"
                      onClick={() => setEmailStage({ bucket: 'funding', label: 'Funding' })}
                    />
                  </div>
                  <FundingMbcbActions
                    profiles={planterProfiles?.funding || []}
                    title="Funding (effective)"
                    listHref="/planters?effectivePlantingStep=funding&journeyEnded=false"
                    accentClassName="border-orange-200 bg-orange-50/60"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs font-medium text-gray-600">Post-funding</div>
                    <EmailGroupButton
                      label="Post-funding"
                      onClick={() => setEmailStage({ bucket: 'post_funding', label: 'Post-funding' })}
                    />
                  </div>
                  <PipelineProfileCloud
                    profiles={planterProfiles?.postFunding || []}
                    title="Post-funding (effective)"
                    listHref="/planters?effectivePlantingStep=post_funding&journeyEnded=false"
                    accentClassName="border-green-200 bg-green-50/60"
                  />
                </div>
              </div>

              {overview.planters.active.needsPath > 0 && (
                <div className="mt-4">
                  <Link
                    to="/planters?pathKindUnset=true&journeyEnded=false"
                    className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                  >
                    Needs pathway data: {overview.planters.active.needsPath} planter
                    {overview.planters.active.needsPath === 1 ? '' : 's'} (no path kind set)
                  </Link>
                  <div className="max-w-md">
                    <PipelineProfileCloud
                      profiles={planterProfiles?.needsPath || []}
                      title="Needs pathway data"
                      listHref="/planters?pathKindUnset=true&journeyEnded=false"
                      accentClassName="border-amber-200 bg-amber-50/70"
                    />
                  </div>
                </div>
              )}

              <h3 className="text-md font-semibold text-gray-800 mb-2 mt-8">Journey ended</h3>
              <p className="text-sm text-gray-600 mb-3">Planters with an ended journey (alumni / exited).</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/planters?journeyEnded=true"
                  className="inline-flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="text-2xl font-bold text-gray-900">{overview.planters.ended.total}</span>
                  <span className="text-sm text-gray-600">All ended</span>
                </Link>
                <Link
                  to="/planters?journeyEnded=true&journeyEndReason=quit"
                  className="inline-flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="text-2xl font-bold text-gray-900">{overview.planters.ended.quit}</span>
                  <span className="text-sm text-gray-600">Quit</span>
                </Link>
                <Link
                  to="/planters?journeyEnded=true&journeyEndReason=fired"
                  className="inline-flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="text-2xl font-bold text-gray-900">{overview.planters.ended.fired}</span>
                  <span className="text-sm text-gray-600">Fired</span>
                </Link>
                <Link
                  to="/planters?journeyEnded=true&journeyEndReason=retired"
                  className="inline-flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="text-2xl font-bold text-gray-900">{overview.planters.ended.retired}</span>
                  <span className="text-sm text-gray-600">Retired</span>
                </Link>
                <Link
                  to="/planters?journeyEnded=true&journeyEndReason=moved_on"
                  className="inline-flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="text-2xl font-bold text-gray-900">{overview.planters.ended.movedOn}</span>
                  <span className="text-sm text-gray-600">Moved on</span>
                </Link>
                {overview.planters.ended.other > 0 && (
                  <div className="inline-flex flex-col rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3 text-left">
                    <span className="text-2xl font-bold text-gray-700">{overview.planters.ended.other}</span>
                    <span className="text-sm text-gray-600">Other / unset reason</span>
                  </div>
                )}
              </div>
            </section>

            <section className="bg-white shadow-sm rounded-xl p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Candidates</h2>
                <span className="text-sm text-gray-500">
                  {overview.portal.prePlanterTotal} total · no church planter profile yet
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Portal candidates whose user account is not linked to a row in church planters.
              </p>
              <div className="flex rounded-lg overflow-hidden border border-gray-200 min-h-[52px]">
                {(() => {
                  const t = overview.portal.prePlanterTotal || 1;
                  return overview.portal.buckets.map((b) => (
                    <Link
                      key={b.key}
                      to={`/onboarding?portalBucket=${encodeURIComponent(b.key)}&prePlanterOnly=true`}
                      className="flex items-center justify-center px-2 py-3 text-center text-sm font-medium text-white transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                      style={{
                        flex: segmentFlex(b.count, t),
                        backgroundColor:
                          b.key === 'early'
                            ? '#4f46e5'
                            : b.key === 'assessment'
                              ? '#0369a1'
                              : b.key === 'passed_awaiting'
                                ? '#15803d'
                                : '#78716c',
                      }}
                      title={`${b.label}: ${b.count}`}
                    >
                      <span className="leading-tight">
                        <span className="block text-lg font-bold">{b.count}</span>
                        <span className="block text-xs font-normal opacity-95">{b.label}</span>
                      </span>
                    </Link>
                  ));
                })()}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {overview.portal.buckets.map((b) => (
                  <div key={b.key}>
                    <div className="text-xs font-medium text-gray-600 mb-1.5 truncate" title={b.label}>
                      {b.label}
                    </div>
                    <PipelineProfileCloud
                      profiles={portalProfilesFor(b.key)}
                      title={b.label}
                      listHref={`/onboarding?portalBucket=${encodeURIComponent(b.key)}&prePlanterOnly=true`}
                      accentClassName={
                        b.key === 'early'
                          ? 'border-indigo-200 bg-indigo-50/60'
                          : b.key === 'assessment'
                            ? 'border-sky-200 bg-sky-50/60'
                            : b.key === 'passed_awaiting'
                              ? 'border-green-200 bg-green-50/60'
                              : 'border-stone-200 bg-stone-50/60'
                      }
                    />
                  </div>
                ))}
              </div>
            </section>

            {nm && (
              <section className="bg-white shadow-sm rounded-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Metrics</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Cross-app rollups. MBCB figures include{' '}
                  <span className="font-medium text-gray-800">submitted</span>,{' '}
                  <span className="font-medium text-gray-800">in review</span>, and{' '}
                  <span className="font-medium text-gray-800">approved</span> reports ({formatInt(nm.mbcb.reportRowsIncluded)}{' '}
                  report rows). Attendance is summed from each plant’s average over the current and previous
                  calendar month (up to two reports per plant). Financial totals sum each included report row.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  <MetricCard
                    label="Logged contacts (hub)"
                    value={formatInt(nm.planterContacts.totalLoggedContacts)}
                    hint="Total contact log entries with planters (calls, visits, emails, etc.)."
                  />
                  <MetricCard
                    label="Planters with logged contact"
                    value={formatInt(nm.planterContacts.plantersWithLoggedContact)}
                    hint="Distinct planters with at least one logged contact."
                  />
                  <MetricCard
                    label="Attendance (plants, last 2 months)"
                    value={
                      Number.isInteger(nm.mbcb.sumPlantAvgSundayAttendance)
                        ? formatInt(nm.mbcb.sumPlantAvgSundayAttendance)
                        : nm.mbcb.sumPlantAvgSundayAttendance.toFixed(1)
                    }
                    hint="For each church plant, average Sunday attendance from up to two MBCB reports (this month and last month), then summed across plants."
                  />
                  <MetricCard
                    label="Budget receipts"
                    value={formatUsd(nm.mbcb.sumBudgetReceiptsMonthly)}
                    hint="Sum of budget receipts per report (monthly income, or income total from giving breakdown when income was not stored)."
                  />
                  <MetricCard
                    label="Mission giving (report total)"
                    value={formatUsd(nm.mbcb.sumMissionGiving)}
                    hint="Sum of mission giving amounts from MBCB report fields."
                  />
                  <MetricCard
                    label="Cooperative Program (CP) giving"
                    value={formatUsd(nm.mbcb.sumCooperativeProgramGiving)}
                    hint="Sum of CP amounts from MBCB giving breakdowns (where captured)."
                  />
                  <MetricCard
                    label="Baptisms reported"
                    value={formatInt(nm.mbcb.sumBaptisms)}
                  />
                  <MetricCard
                    label="Salvations reported"
                    value={formatInt(nm.mbcb.sumSalvations)}
                  />
                  <MetricCard
                    label="Active journey (planters)"
                    value={formatInt(nm.churchPlanters.activeJourney)}
                    hint="Planters whose journey has not ended."
                  />
                </div>
              </section>
            )}

            <p className="text-sm text-gray-500">
              <Link to="/planters" className="text-primary-600 hover:text-primary-700 font-medium">
                Open full planter directory
              </Link>
              {' · '}
              <Link to="/onboarding" className="text-primary-600 hover:text-primary-700 font-medium">
                Portal onboarding list
              </Link>
            </p>
          </div>
        )}
      </div>

      <StageEmailModal
        bucket={emailStage?.bucket ?? 'growth'}
        label={emailStage?.label ?? ''}
        isOpen={emailStage !== null}
        onClose={() => setEmailStage(null)}
      />
    </Layout>
  );
};

export default PlanterPipelineOverview;
